import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import axios from 'axios';
import { useAutosave } from 'react-autosave';
import TextareaAutosize from 'react-textarea-autosize';
import { useAuth } from '../context/AuthContext';
import { Encrypt } from '../utils/encryption';
import { handleMedia } from '../config/api';
import '../styles/Notebook.css';

const Notebook = forwardRef(({
  userId,
  maxPages = 5,
  linesPerPage = 16,
  showHeader = true,
  autosaveInterval = 30000,
  readOnly = false,
}, ref) => {
  const {token} = useAuth();
  const [pages, setPages] = useState([{ content: 'Date: ' }]);
  const [goToPageInput, setGoToPageInput] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [wordCount, setWordCount] = useState(0);

  const maxEditableLines = linesPerPage - 1;
  const lastSerialized = useRef('');

  useEffect(() => {
    const fetchNotes = async () => {
      
      try {
        const response = await axios.post(handleMedia, Encrypt({
          action: 'fetchNotes',
          token: token})
        );

        const raw = response.data.notes || '';
        const parsed = raw
          .split('### NEW_PAGE:')
          .map((block) => block.trim())
          .filter(Boolean)
          .map((page) => ({ content: page }));

        setPages(parsed.length ? parsed : [{ content: 'Date: ' }]);

        // ✅ Set lastSerialized after successful fetch
        lastSerialized.current = parsed
          .map((p) => `### NEW_PAGE:\n${p.content.trim()}`)
          .join('\n');

      } catch (error) {
        console.error('Error fetching notes:', error);
      }
    };

    fetchNotes();
  }, [userId, token]);


  const handleGoToPage = () => {
    const pageNum = parseInt(goToPageInput, 10);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= pages.length) {
      setCurrentPage(pageNum - 1);
    } else {
      alert(`Please enter a number between 1 and ${pages.length}`);
    }
    setGoToPageInput(''); // Clear input after attempt
  };

  const handleSaveNotes = async () => {
    const serialized = pages
      .map((p) => `### NEW_PAGE:\n${p.content.trim()}`)
      .join('\n');

    if (serialized === lastSerialized.current) return; // No change, no save

    try {
      await axios.post(handleMedia, Encrypt({ 
          action: 'saveNotes',
          notes: serialized ,
          token: token
        })
      );
      lastSerialized.current = serialized; // Update after successful save

    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  useAutosave({
    data: pages,
    onSave: handleSaveNotes,
    interval: autosaveInterval,
  });

  const current = pages[currentPage];
  const allLines = current.content.split('\n');
  const [firstLineRaw, ...rest] = allLines;
  const [, ...dateParts] = firstLineRaw.split(':');
  const editableDate = dateParts.join(':').trim();
  const editableText = rest.join('\n');

  useEffect(() => {
    const words = (editableDate + '\n' + editableText).match(/\b\w+\b/g) || [];
    setWordCount(words.length);
  }, [editableDate, editableText]);

  const updateEditableContent = (value) => {
    const lines = value.split('\n');

    if (lines.length > maxEditableLines) {
      alert(`Only ${maxEditableLines} lines allowed (excluding "Date:").`);
      return;
    }

    const newFirstLine = `Date: ${editableDate}`;
    const newPageContent = [newFirstLine, ...lines].join('\n');

    setPages((prev) =>
      prev.map((p, i) => (i === currentPage ? { ...p, content: newPageContent } : p))
    );
  };

  const updateDateContent = (value) => {
    const newFirstLine = `Date: ${value}`;
    const newPageContent = [newFirstLine, ...editableText.split('\n')].join('\n');

    setPages((prev) =>
      prev.map((p, i) => (i === currentPage ? { ...p, content: newPageContent } : p))
    );
  };

  const handlePrevPage = () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < pages.length - 1) setCurrentPage(currentPage + 1);
  };

  const addNewPage = () => {
    if (pages.length >= maxPages) return;

    const today = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

    const newPage = {
      content: `Date: ${today}`,
    };

    setPages([...pages, newPage]);
    setCurrentPage(pages.length);
  };
  
  const deleteCurrentPage = () => {
    if (pages.length <= 1) {
        alert("You must have at least one page.");
        return;
    }

    const updatedPages = pages.filter((_, i) => i !== currentPage);
    const newCurrentPage = currentPage === 0 ? 0 : currentPage - 1;

    setPages(updatedPages);
    setCurrentPage(newCurrentPage);
  };

  useImperativeHandle(ref, () => ({
    handleSaveNotes
  }));

  return (
    <div className="notebook-wrapper">
      {showHeader && <h2>Notebook</h2>}

      <div className="notebook-box">
        <div className="notebook-date-line">
          <strong>Date:</strong>{' '}
          <input
            type="text"
            value={editableDate}
            onChange={(e) => updateDateContent(e.target.value)}
            readOnly={readOnly}
            style={{
              border: 'none',
              outline: 'none',
              fontFamily: 'monospace',
              fontWeight: 'normal',
              background: 'transparent',
              width: '70%',
            }}
          />
        </div>

        <TextareaAutosize
          value={editableText}
          onChange={(e) => updateEditableContent(e.target.value)}
          readOnly={readOnly}
          minRows={maxEditableLines}
          maxRows={maxEditableLines}
          className="notebook-textarea-lined"
        />

        <div className="word-count">Word count: {wordCount}</div>
      </div>

      <div className="notebook-controls">
        <div className="controls-row">
          <div className="go-to-controls">
            <label htmlFor="goto">Go to:</label>
            <input
              id="goto"
              type="number"
              min="1"
              max={pages.length}
              value={goToPageInput}
              onChange={(e) => setGoToPageInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGoToPage();
              }}
            />
            <button onClick={handleGoToPage}>➡️</button>
          </div>

          <div className="nav-buttons">
            <button onClick={handlePrevPage} disabled={currentPage === 0}>◀</button>
            <span>{currentPage + 1} / {maxPages}</span>
            <button onClick={handleNextPage} disabled={currentPage >= pages.length - 1}>▶</button>
          </div>

          {!readOnly && (
            <div className="action-controls">
              <button onClick={handleSaveNotes} title="Save">💾</button>
              {pages.length > 1 && (
                <button onClick={deleteCurrentPage} title="Remove Page">🗑 Page</button>
              )}
              {pages.length < maxPages && (
                <button onClick={addNewPage} title="Add Page">➕ Page</button>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
});

export default Notebook;
